/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAudioPlayback.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing audio playback state and controls.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef } from 'react';
import { playRecording } from '../api';
import { VoiceRecording } from '../types';

/**
 * Hook for audio playback management
 */
export function useAudioPlayback() {
  const [playingRecordingId, setPlayingRecordingId] = useState<number | string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlay = useCallback(
    async (recording: VoiceRecording) => {
      try {
        const idToUse = recording.uuid ?? recording.id;
        
        // If clicking on the same recording that's playing, toggle pause
        if (playingRecordingId === idToUse && audioRef.current) {
          if (isPaused) {
            await audioRef.current.play();
            setIsPaused(false);
          } else {
            audioRef.current.pause();
            setIsPaused(true);
          }
          return;
        }

        // Stop current audio if playing a different recording
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = '';
          audioRef.current = null;
        }

        // Load and play new recording
        const audioBlob = await playRecording(idToUse);
        const audioUrl = URL.createObjectURL(audioBlob);

        const audio = document.createElement('audio');
        audio.src = audioUrl;

        audioRef.current = audio;

        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          audioRef.current = null;
          setPlayingRecordingId(null);
          setIsPaused(false);
        };

        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          audio.remove();
          setPlayingRecordingId(null);
          setIsPaused(false);
          audioRef.current = null;
        };

        setPlayingRecordingId(idToUse);
        setIsPaused(false);
        await audio.play();
      } catch (error) {
        console.error('Failed to play recording:', error);
        setPlayingRecordingId(null);
        setIsPaused(false);
        throw error;
      }
    },
    [playingRecordingId, isPaused],
  );

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setPlayingRecordingId(null);
    setIsPaused(false);
  }, []);

  return {
    playingRecordingId,
    isPaused,
    handlePlay,
    stop,
    isPlaying: (id: number | string) => playingRecordingId === id && !isPaused,
  };
}

